import { Job, Quote, User } from '../types/work';

// Mock current user
export const CURRENT_USER_ID = 'user-1';

// Mock users
export const mockUsers: User[] = [
  { id: 'user-1', name: 'John Doe' },
  { id: 'user-2', name: 'Jane Smith' },
  { id: 'user-3', name: 'Bob Johnson' },
  { id: 'user-4', name: 'Alice Williams' },
  { id: 'user-5', name: 'Charlie Brown' },
];

// Mock jobs
export const mockJobs: Job[] = [
  {
    id: 'job-1',
    title: 'Mobile App Development',
    category: 'Development',
    description: 'Looking for a React Native developer to build a mobile app for our business. Must have experience with Expo and TypeScript.',
    budget: 5000,
    createdBy: 'user-2',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    status: 'open',
  },
  {
    id: 'job-2',
    title: 'Logo Design',
    category: 'Design',
    description: 'Need a professional logo for a tech startup. Looking for modern, clean design that represents innovation.',
    budget: 500,
    createdBy: 'user-3',
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
    status: 'open',
  },
  {
    id: 'job-3',
    title: 'Content Writer Needed',
    category: 'Writing',
    description: 'Seeking an experienced content writer for blog posts about technology and business.',
    createdBy: 'user-4',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    status: 'open',
  },
  {
    id: 'job-4',
    title: 'Website Redesign',
    category: 'Design',
    description: 'Looking to redesign our company website. Need modern UI/UX and responsive design.',
    budget: 3000,
    createdBy: 'user-1', // Posted by current user
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
    status: 'open',
  },
  {
    id: 'job-5',
    title: 'Marketing Campaign Strategy',
    category: 'Marketing',
    description: 'Need help developing a comprehensive marketing campaign for product launch.',
    budget: 2000,
    createdBy: 'user-5',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    status: 'open',
  },
];

// Mock quotes
export const mockQuotes: Quote[] = [
  {
    id: 'quote-1',
    jobId: 'job-1',
    freelancerId: 'user-1', // Current user sent this quote
    freelancerName: 'John Doe',
    amount: 4500,
    message: 'I have 5 years of experience in React Native development. I can deliver a high-quality app within your timeline.',
    status: 'sent',
    createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
  },
  {
    id: 'quote-2',
    jobId: 'job-2',
    freelancerId: 'user-1', // Current user sent this quote
    freelancerName: 'John Doe',
    amount: 450,
    message: 'I specialize in logo design and brand identity. Check out my portfolio!',
    status: 'sent',
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
  },
  {
    id: 'quote-3',
    jobId: 'job-4', // Quote for current user's job
    freelancerId: 'user-2',
    freelancerName: 'Jane Smith',
    amount: 2800,
    message: 'I can redesign your website with a modern look and feel. I have experience with responsive design.',
    status: 'sent',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
  },
  {
    id: 'quote-4',
    jobId: 'job-4', // Another quote for current user's job
    freelancerId: 'user-3',
    freelancerName: 'Bob Johnson',
    amount: 3200,
    message: 'Professional web designer with 10+ years experience. Will provide unlimited revisions.',
    status: 'sent',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
  },
];

// Helper functions
export const getJobById = (id: string): Job | undefined => {
  return mockJobs.find(job => job.id === id);
};

export const getQuotesForJob = (jobId: string): Quote[] => {
  return mockQuotes.filter(quote => quote.jobId === jobId);
};

export const getJobsPostedByUser = (userId: string): Job[] => {
  return mockJobs.filter(job => job.createdBy === userId);
};

export const getQuotesByUser = (userId: string): Quote[] => {
  return mockQuotes.filter(quote => quote.freelancerId === userId);
};

export const getUserById = (id: string): User | undefined => {
  return mockUsers.find(user => user.id === id);
};

export const hasUserQuoted = (jobId: string, userId: string): boolean => {
  return mockQuotes.some(quote => quote.jobId === jobId && quote.freelancerId === userId);
};

// Format relative time
export const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};
